import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { CreateInvoice } from "../components/create-invoice.component";
import { InvoiceNavigation } from "../components/invoice-navigation.component";
import { InvoiceFooter } from "../components/invoice-footer.component";
import { InvoicePreviewModal } from "../components/invoice-preview-modal.component";
import { AccountAccess } from "../components/account-access.component";
import { Footer } from "../components/footer.component";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { getTaxFreeArticleNumber, getTaxFreeArticleUnit } from "../constants/invoice-deductions.constants";
import { transformDeductionToWorkingHour } from "../utils/deduction.utils";
import "../scss/main.scss";
import SectionHeader from "../components/common";
import { useTranslation } from "react-i18next";
import { LOCALES } from "../../common/constants";
import { getSchemaMarkup } from "../../common/FunctionUtils";
import SEOMetadataComponent from "../components/seo-metadata.component";
import { fetchGlobalData } from "../../redux/globalData/globalDataSlice";
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import { createInvoice, createPreliminaryInvoice, createDraftInvoice } from "../../v2/features/invoice/invoice.slice";
import { CreateInvoicePayload, CreateDraftInvoicePayload } from "../../v2/features/invoice/invoice.service";
import { toast } from "react-toastify";
import Header from '../components/header/Header';

import Sidebar from "../components/Sidebar";
import apiCall from "../../api";

export const CreateInvoicePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isPreviousInvoicingTool, setIsPreviousInvoicingTool] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [preId, setPreId] = useState<string | null>(null);
  const [createdDateFromUrl, setCreatedDateFromUrl] = useState<string | null>(null);
  const [invoiceNumberFromUrl, setInvoiceNumberFromUrl] = useState<number>(0);

  // Reference to store invoice data from CreateInvoice component
  const invoiceDataRef = useRef<any>(null);

  // Reference to store validation functions for service descriptions (keyed by freelancerId)
  const validateServiceDescriptionsRef = useRef<{ [freelancerId: string]: () => boolean } | undefined>(undefined);

  // Ref to track previous URL mode (excluding lang parameter)
  const previousUrlModeRef = useRef<{
    hasEdit: boolean;
    hasPreId: boolean;
    hasTimesheet: boolean;
    hasInvoiceNumber: boolean;
  } | null>(null);

  // State to track calculated sub amount from ItemDetails
  const [calculatedSubAmount, setCalculatedSubAmount] = useState<any>(null);

  // State to track deductions by freelancer
  const [deductionsByFreelancer, setDeductionsByFreelancer] = useState<any>({});
  
  // State to track current due date from preferences
  const [currentDueDate, setCurrentDueDate] = useState<string | null>(null);

  // State to track removed copied working hours
  const [removedCopiedWorkingHours, setRemovedCopiedWorkingHours] = useState<Set<number>>(new Set());

  // State to track copied invoice data
  const [copiedInvoiceData, setCopiedInvoiceData] = useState<any>(null);

  // 🆕 TradeLab Tracking State - Track if invoice is already synced
  const [isAlreadySynced, setIsAlreadySynced] = useState<boolean>(true);

  // Get Redux state
  const globalData = useAppSelector((state: any) => state?.globalData?.data);
  const customerState = useAppSelector((state: any) => state?.customer);
  const userDetails = useAppSelector((state: any) => state?.auth?.user);
  const { createLoading, createdInvoice, calculationData, preliminaryLoading, preliminaryInvoice, draftLoading, draftInvoice } = useAppSelector((state: any) => state?.invoice);
  
  // Get timesheet data from Redux
  const timesheetData = useAppSelector((state: any) => state?.timesheet?.timesheetData);
  const timesheetResponse = useAppSelector((state: any) => state?.timesheet);

  // Monitor and log timesheet data when it's available
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const timesheetParam = searchParams.get('timesheet');
    
    if (timesheetParam === 'true') {
      
      if (timesheetData && timesheetData.length > 0) {
      } else {
      }
    }
  }, [timesheetData, timesheetResponse, location.search]);

  // Extract URL parameters and check for edit mode
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isEditMode = searchParams.get('edit');
    const preIdParam = searchParams.get('preId');
    const invoiceNumber = searchParams.get('invoiceNumber');
    const lang = searchParams.get('lang');
    const createdDate = searchParams.get('createdDate');
    const timesheetParam = searchParams.get('timesheet');

    if (isEditMode === 'true') {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }

    // Store preId in state
    if (preIdParam) {
      setPreId(preIdParam);
    }

    // Store invoiceNumber in state (default to 0 if not present)
    if (invoiceNumber) {
      setInvoiceNumberFromUrl(parseInt(invoiceNumber, 10) || 0);
    } else {
      setInvoiceNumberFromUrl(0);
    }

    // Store createdDate in state
    if (createdDate) {
      setCreatedDateFromUrl(createdDate);
    }

    // Check if coming from timesheet and log the data
    if (timesheetParam === 'true') {
    }
  }, [location.search]);

  // Fetch global data on component mount (same as InvoiceTool.js)
  useEffect(() => {
    dispatch(fetchGlobalData() as any);
  }, [dispatch]);

  // Reset state when transitioning FROM edit/timesheet/preId TO fresh creation
  // BUT NOT when only language changes in fresh creation mode
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasEditParam = searchParams.get('edit') === 'true';
    const hasPreIdParam = searchParams.get('preId');
    const hasTimesheetParam = searchParams.get('timesheet') === 'true';
    const hasInvoiceNumber = searchParams.get('invoiceNumber');

    // Current URL mode
    const currentMode = {
      hasEdit: hasEditParam,
      hasPreId: !!hasPreIdParam,
      hasTimesheet: hasTimesheetParam,
      hasInvoiceNumber: !!hasInvoiceNumber
    };

    // Check if this is fresh creation
    const isFreshCreation = !hasEditParam && !hasPreIdParam && !hasTimesheetParam && !hasInvoiceNumber;

    // Check if we had a previous mode
    const hadPreviousMode = previousUrlModeRef.current;

    // Only reset if:
    // 1. First time (no previous mode) AND it's fresh creation
    // 2. OR transitioning FROM a special mode (edit/timesheet/preId) TO fresh creation
    let shouldReset = false;

    if (!hadPreviousMode && isFreshCreation) {
      // First load with fresh creation - reset
      shouldReset = true;
    } else if (hadPreviousMode) {
      const wasInSpecialMode = hadPreviousMode.hasEdit || hadPreviousMode.hasPreId || 
                                hadPreviousMode.hasTimesheet || hadPreviousMode.hasInvoiceNumber;
      
      // If we were in a special mode and now we're in fresh creation - reset
      if (wasInSpecialMode && isFreshCreation) {
        shouldReset = true;
      }
      
      // If mode changed (e.g., from edit to preId, or timesheet to edit) - don't reset
      // This handles navigation between different special modes
    }

    // Update previous mode
    previousUrlModeRef.current = currentMode;

    // Perform reset only if needed
    if (shouldReset) {
      // Reset Redux states
      dispatch({ type: 'invoice/resetInvoiceState' });
      dispatch({ type: 'customer/resetCustomerSelection' });
      dispatch({ type: 'timesheet/resetTimesheetData' });

      // Reset local states
      setCopiedInvoiceData(null);
      setIsEditMode(false);
      setPreId(null);
      setInvoiceNumberFromUrl(0);
      setCreatedDateFromUrl(null);
      setCalculatedSubAmount(null);
      setDeductionsByFreelancer({});
      setRemovedCopiedWorkingHours(new Set());

      // Reset invoice data ref
      if (invoiceDataRef.current) {
        invoiceDataRef.current = null;
      }
    }
  }, [location.search, dispatch]);

  const handleMenuClick = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const currentLanguage = i18n.language;

  const isEnglish = currentLanguage === "en" || currentLanguage === LOCALES.EN_US;
  const canonicalUrl = isEnglish ? `https://www.invozio.com/v2/create-invoice?lang=${LOCALES.EN_US}` : `https://www.invozio.com/v2/create-invoice?lang=${LOCALES.SV_SE}`;

  const schemaMarkup = getSchemaMarkup("create-invoice");

  // Helper function to convert date from MM/DD/YYYY to YYYY/MM/DD format
  const formatDateForAPI = (dateStr: string): string => {
    if (!dateStr) return '';

    // Check if date is already in YYYY/MM/DD or YYYY-MM-DD format
    if (dateStr.match(/^\d{4}[/-]\d{2}[/-]\d{2}$/)) {
      return dateStr.replace(/-/g, '/');
    }

    // Handle MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      // Convert to YYYY/MM/DD
      return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
    }

    return dateStr; // Return as-is if format not recognized
  };

  const handlePreview = async () => {
    try {

      // Get invoice data from ref
      const invoiceData = invoiceDataRef.current;

      if (!invoiceData) {
        toast.error("Please fill in all required invoice details");
        console.error("❌ No invoice data available");
        return;
      }

      // Get customer details
      const selectedCustomer = customerState?.selectedCustomer;
      if (!selectedCustomer || (!selectedCustomer.id && !selectedCustomer.customerId)) {
        toast.error("Please select a customer");
        console.error("❌ No customer selected");
        return;
      }

      const customerId = selectedCustomer.customerId || selectedCustomer.id;

      // Build the same payload as handleSendInvoice
      const { preferences = {}, serviceData = {}, calculatedAmounts = {}, formData = {}, pensionDataByFreelancer = {}, holidayDataByFreelancer = {}, deductionsByFreelancer = {}, selectedFreelancersForInvoice = [] } = invoiceData;

      const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 0;

      // Prepare pension data
      let pensionToSend = false;
      let pensionAmountToSend = 0;

      if (isCollectiveInvoice) {
        const allHavePension = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return pensionDataByFreelancer[freelancerId]?.pension;
        });
        pensionToSend = allHavePension;
        pensionAmountToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const amount = pensionDataByFreelancer[freelancerId]?.pensionAmount || 0;
          return sum + amount;
        }, 0);
      } else {
        const defaultPensionData = pensionDataByFreelancer["default"];
        pensionToSend = defaultPensionData?.pension || false;
        pensionAmountToSend = defaultPensionData?.pensionAmount || 0;
      }

      // Prepare holiday savings data
      let holidaySavingsToSend = false;
      let holidaySavingsFeeToSend = 0;

      if (isCollectiveInvoice) {
        const allHaveHolidaySavings = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return holidayDataByFreelancer[freelancerId]?.holidaySavings;
        });
        holidaySavingsToSend = allHaveHolidaySavings;
        holidaySavingsFeeToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const fee = holidayDataByFreelancer[freelancerId]?.holidaySavingsFee || 0;
          return sum + fee;
        }, 0);
      } else {
        const defaultHolidayData = holidayDataByFreelancer["default"];
        holidaySavingsToSend = defaultHolidayData?.holidaySavings || false;
        holidaySavingsFeeToSend = defaultHolidayData?.holidaySavingsFee || 0;
      }

      // Prepare working hours data
      let totalWorkingDates: any[] = [];
      let totalAmountFromWorkingHours = 0;

      // Using imported transformDeductionToWorkingHour from deduction.utils.ts
      // This function correctly sets article numbers based on isTaxFreeArticle flag

      if (invoiceData.serviceTableDataByFreelancer) {
        for (const freelancerId of Object.keys(invoiceData.serviceTableDataByFreelancer)) {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const serviceWorkingHours = freelancerData?.workingHours || [];
          const freelancerDeductions = deductionsByFreelancer[freelancerId] || [];
          const deductionWorkingHours: any[] = [];
          const currentVat = preferences?.vat || 25;
          const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

          for (const deduction of freelancerDeductions) {
            try {
              const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
              deductionWorkingHours.push(deductionWH);
            } catch (error) {
              console.error('Error transforming deduction:', error, deduction);
            }
          }

          const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];
          const workingHoursWithUserId = allWorkingHours.map((wh: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
              userId: freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId,
            };
          });
          totalWorkingDates = [...totalWorkingDates, ...workingHoursWithUserId];
        }
      } else if (serviceData?.workingHours) {
        const serviceWorkingHours = serviceData.workingHours || [];
        const freelancerDeductions = deductionsByFreelancer['default'] || [];
        const deductionWorkingHours: any[] = [];
        const currentVat = preferences?.vat || 25;
        const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

        for (const deduction of freelancerDeductions) {
          try {
            const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
            deductionWorkingHours.push(deductionWH);
          } catch (error) {
            console.error('Error transforming deduction:', error, deduction);
          }
        }

        const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];
        totalWorkingDates = allWorkingHours.map((wh: any) => {
          totalAmountFromWorkingHours += Number(wh.amount || 0);
          return {
            ...wh,
            id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
            userId: userDetails?.userId || userDetails?.id,
          };
        });
      }

      // Add working hours from copiedInvoiceData that haven't been removed (non-service articles only)
      // Get removedCopiedWorkingHours from invoiceData (passed via dataRef)
      const removedSet = invoiceData.removedCopiedWorkingHours || new Set();
      if (copiedInvoiceData?.workingHours && Array.isArray(copiedInvoiceData.workingHours)) {
        const currentVat = preferences?.vat || 25; // Get current selected VAT
        const copiedNonServiceWorkingHours = copiedInvoiceData.workingHours
          .map((wh: any, index: number) => ({ wh, originalIndex: index }))
          .filter(({ wh, originalIndex }: any) => {
            // Only include non-service articles (not 92, 94, 95, 96, 97)
            const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
            // Check if this working hour hasn't been removed
            const isNotRemoved = !removedSet.has(originalIndex);
            return !isServiceArticle && isNotRemoved;
          })
          .map(({ wh }: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID from copied invoice
              vat: wh.vat != null ? wh.vat : currentVat, // Preserve original VAT if exists
            };
          });



        totalWorkingDates = [...totalWorkingDates, ...copiedNonServiceWorkingHours];
      }

      // Prepare invoice freelancer array
      const invoiceFreelancerArr: any[] = [];
      if (invoiceData.serviceTableDataByFreelancer) {
        // Create a Set to track unique freelancer IDs
        const addedFreelancerIds = new Set<string>();

        Object.keys(invoiceData.serviceTableDataByFreelancer).forEach((freelancerId) => {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const workingHours = freelancerData?.workingHours || [];

          // Calculate amount for this freelancer (sum of their working hours subtotals)
          // Amount will be 0 if no working hours exist
          const freelancerAmount = workingHours.reduce((sum: number, wh: any) => {
            return sum + Number(wh.amount || 0);
          }, 0);
          const pensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer["default"] || {};
          const holidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer["default"] || {};
          const actualUserId = freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId;

          // Convert to string to ensure consistent comparison
          const userIdStr = actualUserId?.toString();

          // Skip if this freelancer has already been added
          if (addedFreelancerIds.has(userIdStr)) {
            return;
          }

          // Add to set to track
          addedFreelancerIds.add(userIdStr);

          invoiceFreelancerArr.push({
            userId: actualUserId,
            freelancerId: actualUserId,
            amount: freelancerAmount,
            pension: pensionData?.pension || false,
            pensionAmount: pensionData?.pensionAmount || 0,
            holidaySavings: holidayData?.holidaySavings || false,
            holidaySavingsFee: holidayData?.holidaySavingsFee || 0,
          });
        });
      }

      const deductionType = preferences?.deductionType || "none";
      const isROT = deductionType === "rot";
      const isRUT = deductionType === "rut";

      // Determine multipleFreelancer based on invoiceFreelancerArr length
      const multipleFreelancer = invoiceFreelancerArr.length > 1;

      // Update VAT in all working hours to match current preference
      // BUT preserve original VAT if it exists (for tax-free articles and deductions)
      const currentVatValue = preferences?.vat || 25;
      const updatedWorkingHours = totalWorkingDates.map((wh: any) => {
        return {
          ...wh,
          vat: wh.vat != null ? wh.vat : currentVatValue, // Preserve original VAT if exists
        };
      });

      // Build the payload
      const previewPayload: CreateInvoicePayload = {
        approvalInvoiceId: 0,
        rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
        rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
        invoiceId: 0,
        sendInvoice: 1,
        vat: preferences?.vat || 25,
        pension: pensionToSend,
        amount: totalAmountFromWorkingHours || 0,
        isFixed: formData?.isFixed || false,
        currency: preferences?.currency || "SEK",
        brfNo: preferences?.brfOrgNumber || "",
        registerAssignment: false,
        showDates: preferences?.showDates === 'yes' || preferences?.showDates === true ? true : false,
        house: preferences?.accommodationType === "house",
        holidaySavings: holidaySavingsToSend,
        description: formData?.otherDetails?.customerNotes || formData?.description || "",
        taxValue: calculatedAmounts?.tax || 0,
        totalToPay: calculatedAmounts?.net || 0,
        rutDeduction: isRUT,
        rootDeduction: isROT,
        file: formData?.otherDetails?.supportingFiles?.[0] || null,
        expressPayout: formData?.expressPayout || false,
        workingHours: updatedWorkingHours,
        email: selectedCustomer.email || "",
        apartment: preferences?.accommodationType === "apartment" || (isROT && preferences?.accommodationType !== "house"),
        pensionFee: pensionToSend ? calculatedAmounts?.pensionPercentage || 0 : 0,
        holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
        tax: userDetails?.incomeTax || 0,
        rotApartmentNo: preferences?.apartmentHouseNo || "",
        pensionAmount: pensionAmountToSend,
        customerId: customerId,
        expenditureForMaterialsAndToolsTaxFreeAmount: 0,
        holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
        amountIncVat: Math.round(calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || calculatedAmounts?.amountIncludingVat || 0),
        secondaryEmailAddress: selectedCustomer.secondaryEmailAddress || "",
        multipleFreelancer: multipleFreelancer,
        currencyRate: globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || "1",
        invoiceFreelancer: invoiceFreelancerArr,
        locale: i18n.language || "en-us",
      };

      // Call preliminary invoice API
      const result = await dispatch(createPreliminaryInvoice(previewPayload)).unwrap();


      // Open preview modal after successful API call
      setShowPreviewModal(true);
    } catch (error: any) {
      console.error("❌ Create Preliminary Invoice Error:", error);
      // Error toast already shown in Redux reducer
    }
  };

  const handleSaveDraft = () => {

  };

  const handleSaveAndShare = () => {

  };

  // Function to build invoice payload for API calls
  const buildInvoicePayload = async () => {
    try {
      const invoiceData = invoiceDataRef.current;


      if (!invoiceData) {
        console.error("❌ No invoice data available");
        return null;
      }

      const selectedCustomer = customerState?.selectedCustomer;
      if (!selectedCustomer || (!selectedCustomer.id && !selectedCustomer.customerId)) {
        console.error("❌ No customer selected");
        return null;
      }

      const customerId = selectedCustomer.customerId || selectedCustomer.id;
      const { preferences = {}, serviceData = {}, calculatedAmounts = {}, formData = {}, pensionDataByFreelancer = {}, holidayDataByFreelancer = {}, deductionsByFreelancer = {}, selectedFreelancersForInvoice = [] } = invoiceData;

      const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 0;

      // Prepare pension data
      let pensionToSend = false;
      let pensionAmountToSend = 0;

      if (isCollectiveInvoice) {
        const allHavePension = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return pensionDataByFreelancer[freelancerId]?.pension;
        });
        pensionToSend = allHavePension;
        pensionAmountToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const amount = pensionDataByFreelancer[freelancerId]?.pensionAmount || 0;
          return sum + amount;
        }, 0);
      } else {
        const defaultPensionData = pensionDataByFreelancer["default"];
        pensionToSend = defaultPensionData?.pension || false;
        pensionAmountToSend = defaultPensionData?.pensionAmount || 0;
      }

      // Prepare holiday savings data
      let holidaySavingsToSend = false;
      let holidaySavingsFeeToSend = 0;

      if (isCollectiveInvoice) {
        const allHaveHolidaySavings = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return holidayDataByFreelancer[freelancerId]?.holidaySavings;
        });
        holidaySavingsToSend = allHaveHolidaySavings;
        holidaySavingsFeeToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const fee = holidayDataByFreelancer[freelancerId]?.holidaySavingsFee || 0;
          return sum + fee;
        }, 0);
      } else {
        const defaultHolidayData = holidayDataByFreelancer["default"];
        holidaySavingsToSend = defaultHolidayData?.holidaySavings || false;
        holidaySavingsFeeToSend = defaultHolidayData?.holidaySavingsFee || 0;
      }

      let totalWorkingDates: any[] = [];
      let totalAmountFromWorkingHours = 0;
      const invoiceFreelancerArr: any[] = [];

      // Using imported transformDeductionToWorkingHour from deduction.utils.ts
      // This function correctly sets article numbers based on isTaxFreeArticle flag

      // Process working hours from serviceTableDataByFreelancer or serviceData
      if (invoiceData.serviceTableDataByFreelancer) {
        for (const freelancerId of Object.keys(invoiceData.serviceTableDataByFreelancer)) {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const serviceWorkingHours = freelancerData?.workingHours || [];
          const freelancerDeductions = deductionsByFreelancer[freelancerId] || [];
          const deductionWorkingHours: any[] = [];
          const currentVat = preferences?.vat || 25;
          const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

          for (const deduction of freelancerDeductions) {
            try {
              const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
              deductionWorkingHours.push(deductionWH);
            } catch (error) {
              console.error('Error transforming deduction:', error, deduction);
            }
          }

          const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];
          const workingHoursWithUserId = allWorkingHours.map((wh: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
              userId: freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId,
            };
          });
          totalWorkingDates = [...totalWorkingDates, ...workingHoursWithUserId];
        }
      } else if (serviceData?.workingHours) {
        const serviceWorkingHours = serviceData.workingHours || [];
        const freelancerDeductions = deductionsByFreelancer['default'] || [];
        const deductionWorkingHours: any[] = [];
        const currentVat = preferences?.vat || 25;
        const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

        for (const deduction of freelancerDeductions) {
          try {
            const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
            deductionWorkingHours.push(deductionWH);
          } catch (error) {
            console.error('Error transforming deduction:', error, deduction);
          }
        }

        const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];
        totalWorkingDates = allWorkingHours.map((wh: any) => {
          totalAmountFromWorkingHours += Number(wh.amount || 0);
          return {
            ...wh,
            id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
            userId: userDetails?.userId || userDetails?.id,
          };
        });
      }

      // Add working hours from copiedInvoiceData that haven't been removed (non-service articles only)
      if (copiedInvoiceData?.workingHours && Array.isArray(copiedInvoiceData.workingHours)) {
        const currentVat = preferences?.vat || 25; // Get current selected VAT
        // Get removedCopiedWorkingHours from invoiceData (passed via dataRef) - FIX for Save Draft
        const removedSet = invoiceData.removedCopiedWorkingHours || new Set();
        const copiedNonServiceWorkingHours = copiedInvoiceData.workingHours
          .map((wh: any, index: number) => ({ wh, originalIndex: index }))
          .filter(({ wh, originalIndex }: any) => {
            // Only include non-service articles (not 92, 94, 95, 96, 97)
            const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
            // Check if this working hour hasn't been removed - USE removedSet from dataRef
            const isNotRemoved = !removedSet.has(originalIndex);
            return !isServiceArticle && isNotRemoved;
          })
          .map(({ wh }: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID from copied invoice
              vat: wh.vat != null ? wh.vat : currentVat, // Preserve original VAT if exists
            };
          });



        totalWorkingDates = [...totalWorkingDates, ...copiedNonServiceWorkingHours];
      }

      // Prepare invoice freelancer array
      if (invoiceData.serviceTableDataByFreelancer) {
        // Create a Set to track unique freelancer IDs
        const addedFreelancerIds = new Set<string>();

        Object.keys(invoiceData.serviceTableDataByFreelancer).forEach((freelancerId) => {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const workingHours = freelancerData?.workingHours || [];

          // Calculate amount for this freelancer (sum of their working hours subtotals)
          // Amount will be 0 if no working hours exist
          const freelancerAmount = workingHours.reduce((sum: number, wh: any) => {
            return sum + Number(wh.amount || 0);
          }, 0);
          const pensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer["default"] || {};
          const holidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer["default"] || {};
          const actualUserId = freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId;

          // Convert to string to ensure consistent comparison
          const userIdStr = actualUserId?.toString();

          // Skip if this freelancer has already been added
          if (addedFreelancerIds.has(userIdStr)) {
            return;
          }

          // Add to set to track
          addedFreelancerIds.add(userIdStr);

          invoiceFreelancerArr.push({
            userId: actualUserId,
            freelancerId: actualUserId,
            amount: freelancerAmount,
            pension: pensionData?.pension || false,
            pensionAmount: pensionData?.pensionAmount || 0,
            holidaySavings: holidayData?.holidaySavings || false,
            holidaySavingsFee: holidayData?.holidaySavingsFee || 0,
          });
        });
      }


      const deductionType = preferences?.deductionType || "none";
      const isROT = deductionType === "rot";
      const isRUT = deductionType === "rut";

      const multipleFreelancer = invoiceFreelancerArr.length > 1;

      // Update VAT in all working hours to match current preference
      // BUT preserve original VAT if it exists (for tax-free articles and deductions)
      const currentVatValue = preferences?.vat || 25;
      const updatedWorkingHours = totalWorkingDates.map((wh: any) => {
        return {
          ...wh,
          vat: wh.vat != null ? wh.vat : currentVatValue, // Preserve original VAT if exists
        };
      });



      const payload: CreateInvoicePayload = {
        approvalInvoiceId: 0,
        rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
        rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
        invoiceId: preId ? parseInt(preId, 10) : 0, // Use preId from URL if available
        sendInvoice: 1,
        vat: preferences?.vat || 25,
        pension: pensionToSend,
        amount: totalAmountFromWorkingHours || 0,
        isFixed: formData?.isFixed || false,
        currency: preferences?.currency || "SEK",
        brfNo: preferences?.brfOrgNumber || "",
        registerAssignment: false,
        showDates: preferences?.showDates === 'yes' || preferences?.showDates === true ? true : false,
        house: preferences?.accommodationType === "house",
        holidaySavings: holidaySavingsToSend,
        description: formData?.otherDetails?.customerNotes || formData?.description || "",
        taxValue: calculatedAmounts?.tax || 0,
        totalToPay: calculatedAmounts?.net || 0,
        rutDeduction: isRUT,
        rootDeduction: isROT,
        file: formData?.otherDetails?.supportingFiles?.length > 0
          ? formData.otherDetails.supportingFiles.map((f: any) => ({
            fileName: f.fileName,
            fileContent: f.fileContent
          }))
          : [],
        expressPayout: formData?.expressPayout || false,
        expressFee: (formData?.expressPayout || false) ? 5.9 : 0,
        workingHours: updatedWorkingHours,
        email: selectedCustomer.email || "",
        apartment: preferences?.accommodationType === "apartment" || (isROT && preferences?.accommodationType !== "house"),
        pensionFee: pensionToSend ? calculatedAmounts?.pensionPercentage || 0 : 0,
        holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
        tax: userDetails?.incomeTax || 0,
        rotApartmentNo: preferences?.apartmentHouseNo || "",
        pensionAmount: pensionAmountToSend,
        customerId: customerId,
        expenditureForMaterialsAndToolsTaxFreeAmount: 0,
        holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
        amountIncVat: Math.round(calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || calculatedAmounts?.amountIncludingVat || 0),
        secondaryEmailAddress: selectedCustomer.secondaryEmailAddress || "",
        multipleFreelancer: multipleFreelancer,
        currencyRate: globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || "1",
        invoiceFreelancer: invoiceFreelancerArr,
        locale: i18n.language || "en-us",
        createdDate: createdDateFromUrl || new Date().toISOString(),
        dueDate: preferences?.dueDate || "", // NEW - Due date from preferences
      };

      return payload;
    } catch (error) {
      console.error("❌ Error building invoice payload:", error);
      return null;
    }
  };

  // Function to handle second API call with PDF blob
  const handleSecondApiCall = async (pdfBlob: Blob): Promise<void> => {
    try {
      // Build the same payload as used in first API call
      const payload = await buildInvoicePayload();


      if (!payload) {
        toast.error("Unable to build invoice payload");
        throw new Error("Unable to build invoice payload");
      }

      // Convert PDF blob to base64
      const pdfBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove the data URL prefix (data:application/pdf;base64,)
          const base64 = base64String.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      // Create the final payload with PDF - same structure as /invoice/new-add-invoice
      const draftPayload: CreateDraftInvoicePayload = {
        ...payload,
        invoicePdf: pdfBase64, // Add PDF as base64 string
      };



      // Call the draft invoice API using Redux action
      const result = await dispatch(createDraftInvoice(draftPayload)).unwrap();


      // Navigate to draft invoice page after success
      const locale = i18n.language || 'en-us';
      window.location.href = `/v2/draft-invoice?lang=${locale}`;
    } catch (error) {
      console.error("❌ Second API Error:", error);
      throw error;
    }
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleSendInvoice = async (): Promise<any> => {
    try {

      // Get invoice data from ref (will be set by CreateInvoice component)
      const invoiceData = invoiceDataRef.current;

      if (!invoiceData) {
        toast.error("Please fill in all required invoice details");
        console.error("❌ No invoice data available");
        return false;
      }

      // Validate service descriptions for all freelancers before proceeding
      if (validateServiceDescriptionsRef.current) {
        const validationFunctions = validateServiceDescriptionsRef.current;
        const freelancerIds = Object.keys(validationFunctions);

        for (const freelancerId of freelancerIds) {
          const validateFunc = validationFunctions[freelancerId];
          if (validateFunc && !validateFunc()) {
            toast.error(String(t("PLEASE_FILL_SERVICE_DESCRIPTION") || "Please fill in service description for all working hours"));
            console.error(`❌ Service description validation failed for freelancer: ${freelancerId}`);
            return false;
          }
        }
      }

      // Validate description (customer notes) before proceeding
      const description = invoiceData?.formData?.otherDetails?.customerNotes || invoiceData?.formData?.description || "";

      if (!description || description.trim().length === 0) {
        toast.error(String(t("PLEASE_ENTER_DESCRIPTION_BEFORE_CREATING_INVOICE") || "Please enter customer notes (description) before creating invoice"));
        console.error("❌ Description is required");
        return false;
      }

      // Validate description length and word count
      const descriptionLength = description.trim().length;
      const wordCount = description.trim().split(/\s+/).filter((word: string) => word.length > 0).length;

      if (descriptionLength < 20 && wordCount < 3) {
        toast.error(String(t("Enter at least 20 characters or 3 words") || "Enter at least 20 characters or 3 words in customer notes"));
        console.error("❌ Description too short");
        return false;
      }

      // Get customer details
      const selectedCustomer = customerState?.selectedCustomer;

      if (!selectedCustomer || (!selectedCustomer.id && !selectedCustomer.customerId)) {
        toast.error("Please select a customer");
        console.error("❌ No customer selected");
        return false;
      }

      // Use customerId or id (whichever is available)
      const customerId = selectedCustomer.customerId || selectedCustomer.id;

      // Extract data from invoice
      const { preferences = {}, serviceData = {}, calculatedAmounts = {}, formData = {}, pensionDataByFreelancer = {}, holidayDataByFreelancer = {}, deductionsByFreelancer = {}, selectedFreelancersForInvoice = [] } = invoiceData;

      // Check if it's a collective invoice
      const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 0;

      // Prepare pension data
      let pensionToSend = false;
      let pensionAmountToSend = 0;

      if (isCollectiveInvoice) {
        const allHavePension = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return pensionDataByFreelancer[freelancerId]?.pension;
        });
        pensionToSend = allHavePension;

        pensionAmountToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const amount = pensionDataByFreelancer[freelancerId]?.pensionAmount || 0;
          return sum + amount;
        }, 0);
      } else {
        const defaultPensionData = pensionDataByFreelancer["default"];
        pensionToSend = defaultPensionData?.pension || false;
        pensionAmountToSend = defaultPensionData?.pensionAmount || 0;
      }

      // Prepare holiday savings data
      let holidaySavingsToSend = false;
      let holidaySavingsFeeToSend = 0;

      if (isCollectiveInvoice) {
        const allHaveHolidaySavings = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return holidayDataByFreelancer[freelancerId]?.holidaySavings;
        });
        holidaySavingsToSend = allHaveHolidaySavings;

        holidaySavingsFeeToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const fee = holidayDataByFreelancer[freelancerId]?.holidaySavingsFee || 0;
          return sum + fee;
        }, 0);
      } else {
        const defaultHolidayData = holidayDataByFreelancer["default"];
        holidaySavingsToSend = defaultHolidayData?.holidaySavings || false;
        holidaySavingsFeeToSend = defaultHolidayData?.holidaySavingsFee || 0;
      }

      // Prepare working hours data from service table
      // Combine all working hours from all freelancers into one array with userId
      let totalWorkingDates: any[] = [];
      let totalAmountFromWorkingHours = 0; // NEW - Calculate total amount from all working hours


      // Using imported transformDeductionToWorkingHour from deduction.utils.ts
      // This function correctly sets article numbers based on isTaxFreeArticle flag

      if (invoiceData.serviceTableDataByFreelancer) {
        // For collective invoice or service table data grouped by freelancer
        for (const freelancerId of Object.keys(invoiceData.serviceTableDataByFreelancer)) {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const serviceWorkingHours = freelancerData?.workingHours || [];

          // Get deductions for this freelancer and transform to working hours
          const freelancerDeductions = deductionsByFreelancer[freelancerId] || [];
          const deductionWorkingHours: any[] = [];

          const currentVat = preferences?.vat || 25;
          const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

          for (const deduction of freelancerDeductions) {
            try {
              const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
              deductionWorkingHours.push(deductionWH);
            } catch (error) {
              console.error('Error transforming deduction:', error, deduction);
            }
          }

          // Merge service working hours with deduction working hours
          const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];

          // Add userId to each working hour entry and calculate total amount
          const workingHoursWithUserId = allWorkingHours.map((wh: any) => {
            // Sum up the amount (subtotal) from each working hour
            totalAmountFromWorkingHours += Number(wh.amount || 0);

            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
              userId: freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId,
            };
          });

          totalWorkingDates = [...totalWorkingDates, ...workingHoursWithUserId];
        }
      } else if (serviceData?.workingHours) {
        // Fallback: if working hours are directly in serviceData
        const serviceWorkingHours = serviceData.workingHours || [];

        // Get deductions for default freelancer
        const freelancerDeductions = deductionsByFreelancer['default'] || [];
        const deductionWorkingHours: any[] = [];

        const currentVat = preferences?.vat || 25;
        const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

        for (const deduction of freelancerDeductions) {
          try {
            const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
            deductionWorkingHours.push(deductionWH);
          } catch (error) {
            console.error('Error transforming deduction:', error, deduction);
          }
        }


        // Merge service working hours with deduction working hours
        const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];

        totalWorkingDates = allWorkingHours.map((wh: any) => {
          totalAmountFromWorkingHours += Number(wh.amount || 0);

          return {
            ...wh,
            id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
            userId: userDetails?.userId || userDetails?.id,
          };
        });
      }

      // Add working hours from copiedInvoiceData that haven't been removed (non-service articles only)
      // Get removedCopiedWorkingHours from invoiceData (passed via dataRef)
      const removedSet = invoiceData.removedCopiedWorkingHours || new Set();
      if (copiedInvoiceData?.workingHours && Array.isArray(copiedInvoiceData.workingHours)) {
        const currentVat = preferences?.vat || 25; // Get current selected VAT
        const copiedNonServiceWorkingHours = copiedInvoiceData.workingHours
          .map((wh: any, index: number) => ({ wh, originalIndex: index }))
          .filter(({ wh, originalIndex }: any) => {
            // Only include non-service articles (not 92, 94, 95, 96, 97)
            const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
            // Check if this working hour hasn't been removed
            const isNotRemoved = !removedSet.has(originalIndex);
            return !isServiceArticle && isNotRemoved;
          })
          .map(({ wh }: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID from copied invoice
              vat: wh.vat != null ? wh.vat : currentVat, // Preserve original VAT if exists
            };
          });

        totalWorkingDates = [...totalWorkingDates, ...copiedNonServiceWorkingHours];
      }



      // Prepare invoice freelancer array - for both single and collective invoices
      const invoiceFreelancerArr: any[] = [];

      if (invoiceData.serviceTableDataByFreelancer) {
        // Create a Set to track unique freelancer IDs
        const addedFreelancerIds = new Set<string>();

        // Iterate through each freelancer's service table data
        Object.keys(invoiceData.serviceTableDataByFreelancer).forEach((freelancerId) => {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const workingHours = freelancerData?.workingHours || [];

          // Calculate amount for this freelancer (sum of their working hours subtotals)
          // Amount will be 0 if no working hours exist
          const freelancerAmount = workingHours.reduce((sum: number, wh: any) => {
            return sum + Number(wh.amount || 0);
          }, 0);

          // Get pension data for this freelancer
          const pensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer["default"] || {};
          const holidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer["default"] || {};

          // Get actual userId (for 'default', use current user's ID)
          const actualUserId = freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId;

          // Convert to string to ensure consistent comparison
          const userIdStr = actualUserId?.toString();

          // Skip if this freelancer has already been added
          if (addedFreelancerIds.has(userIdStr)) {
            return;
          }

          // Add to set to track
          addedFreelancerIds.add(userIdStr);

          invoiceFreelancerArr.push({
            userId: actualUserId,
            freelancerId: actualUserId,
            amount: freelancerAmount,
            pension: pensionData?.pension || false,
            pensionAmount: pensionData?.pensionAmount || 0,
            holidaySavings: holidayData?.holidaySavings || false,
            holidaySavingsFee: holidayData?.holidaySavingsFee || 0,
          });
        });
      }


      // Get deduction type from preferences
      const deductionType = preferences?.deductionType || "none";
      const isROT = deductionType === "rot";
      const isRUT = deductionType === "rut";

      // Determine multipleFreelancer based on invoiceFreelancerArr length
      const multipleFreelancer = invoiceFreelancerArr.length > 1;

      // Update VAT in all working hours to match current preference
      // BUT preserve original VAT if it exists (for tax-free articles and deductions)
      const currentVatValue = preferences?.vat || 25;
      const updatedWorkingHours = totalWorkingDates.map((wh: any) => {
       
        return {
          ...wh,
          vat: wh.vat != null ? wh.vat : currentVatValue, // Preserve original VAT if exists, else use current preference
        };
      });

      // Build the payload
      const payload: CreateInvoicePayload = {
        approvalInvoiceId: 0,
        rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
        rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
        invoiceId: 0,
        sendInvoice: 1,
        vat: preferences?.vat || 25,
        draftId: preId || undefined,  // Pass preId as draftId if exists
        isDraft: preId ? true : false,  // Set isDraft to true if preId exists, otherwise false
        pension: pensionToSend,
        amount: totalAmountFromWorkingHours || 0, // Use calculated total from all working hours subtotals
        isFixed: formData?.isFixed || false,
        currency: preferences?.currency || "SEK",
        brfNo: preferences?.brfOrgNumber || "",
        registerAssignment: false,
        showDates: preferences?.showDates === 'yes' || preferences?.showDates === true ? true : false,
        house: preferences?.accommodationType === "house",
        holidaySavings: holidaySavingsToSend,
        description: formData?.otherDetails?.customerNotes || formData?.description || "",
        taxValue: calculatedAmounts?.tax || 0,
        totalToPay: calculatedAmounts?.net || 0,
        rutDeduction: isRUT,
        rootDeduction: isROT,
        file: formData?.otherDetails?.supportingFiles?.length > 0
          ? formData.otherDetails.supportingFiles.map((f: any) => ({
            fileName: f.fileName,
            fileContent: f.fileContent
          }))
          : [],
        expressPayout: formData?.expressPayout || false,
        expressFee: (formData?.expressPayout || false) ? 5.9 : 0,
        workingHours: updatedWorkingHours,
        email: selectedCustomer.email || "",
        apartment: preferences?.accommodationType === "apartment" || (isROT && preferences?.accommodationType !== "house"),
        pensionFee: pensionToSend ? calculatedAmounts?.pensionPercentage || 0 : 0,
        holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
        tax: userDetails?.incomeTax || 0,
        rotApartmentNo: preferences?.apartmentHouseNo || "",
        pensionAmount: pensionAmountToSend,
        customerId: customerId, // Use the extracted customerId
        expenditureForMaterialsAndToolsTaxFreeAmount: 0,
        holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
        amountIncVat: Math.round(calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || calculatedAmounts?.amountIncludingVat || 0),
        secondaryEmailAddress: selectedCustomer.secondaryEmailAddress || "",
        multipleFreelancer: multipleFreelancer,
        currencyRate: globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || "1",
        invoiceFreelancer: invoiceFreelancerArr,
        locale: i18n.language || "en-us",
        dueDate: preferences?.dueDate || "", // NEW - Due date from preferences
      };

      // Dispatch the appropriate action based on edit mode
      let result;
      if (isEditMode) {
        // Extract invoice number from URL for edit mode
        const searchParams = new URLSearchParams(location.search);
        const invoiceNumber = searchParams.get('invoiceNumber');
        // Add invoice number to payload for edit mode
        const editPayload = {
          ...payload,
          invoiceNumber: invoiceNumber ? parseInt(invoiceNumber) : 0,
          dueDate: payload.dueDate || "", // Explicitly include dueDate
          createdDate: createdDateFromUrl || payload.createdDate || new Date().toISOString(), // Explicitly include createdDate
        };

        // Call the edit invoice API directly
        const response = await apiCall.post('/editinvoiceV2/invoice-edit-request-v2', editPayload);
        result = response.data;


        if (result.statusCode === 200 || result.statusCode === 201) {
          toast.success(String(t('INVOICE_EDITED_SUCCESSFULLY')));
        }
      } else {
        result = await dispatch(createInvoice(payload)).unwrap();

        // 🆕 Extract isAlreadySynced from API response for TradeLab tracking
        if (result && typeof result === 'object' && result?.response?.isAlreadySynced !== undefined) {
          setIsAlreadySynced(result.response.isAlreadySynced);
        }
      }

      // Return full result object so modal can extract isAlreadySynced
      return result;
    } catch (error: any) {
      console.error("❌ Create Invoice Error:", error);
      // Error toast is already shown in Redux reducer
      return false;
    }
  };

  // 🆕 Check if environment is production
 
  // const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname === 'invozio.com' || window.location.hostname === 'www.invozio.com';

  // 🆕 Get userId for TradeLab tracking
  const userId = userDetails?.userId || userDetails?.id || '';

  return (
    <>
      {/* SEO: Canonical URL */}
      <Helmet>
        <link
          rel="canonical"
          href={i18n.language === LOCALES.EN_US ? "https://invozio.com/v2/create-invoice" : "https://invozio.com/v2/create-invoice"}
        />
      </Helmet>

      {/* 🆕 TradeLab Conversion Tracking - Fire only on production & first-time invoice */}
      {!isAlreadySynced && userId && (() => {
       
        return (
          <Helmet>
            <script type="text/javascript">
              {` 
                if (typeof tdconv !== "undefined" && tdconv !== null) {
                  tdconv('init', '2358708', {'element': 'iframe'});
                  tdconv('track', 'sale', {
                    'transactionId': '${userId}',
                    'ordervalue': '0',
                    'currency': 'SEK',
                    'event': 437992
                  });
                 
              `}
            </script>
          </Helmet>
        );
      })()}

      <div className="dashboard-page">
        <div className="dashboard-layout">
          <Sidebar activeItemId="create-invoices" footer={null}
            onClose={() => setIsSidebarOpen(false)}
            className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
          />
          {isSidebarOpen && (
            <div
              className="sidebar-overlay"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
          {/* {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />} */}

          <div className="dashboard-main-content">
            <Header
              setIsSidebarOpen={setIsSidebarOpen}
              isCreateInvoicePage={true} />
            <div className="create-invoice-page main-content-wrapper" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
              {/* <Header /> */}

              <div className="">
                 <InvoiceNavigation
                      onPreview={handlePreview}
                      onSaveDraft={handleSaveDraft}
                      onSaveAndShare={handleSaveAndShare}
                      isPreviousInvoicingTool={isPreviousInvoicingTool}
                      onTogglePreviousInvoicingTool={setIsPreviousInvoicingTool}
                      isEditMode={isEditMode}
                      isPreviewLoading={preliminaryLoading}
                      getPayload={buildInvoicePayload}
                      onSecondApiCall={handleSecondApiCall}
                      validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                      dataRef={invoiceDataRef}
                    />
                <div className="row">
                  <div className="col-12">
                   

                    {/* Keep CreateInvoice mounted to preserve data */}
                    <div style={{ display: showPreviewModal ? 'none' : 'block' }}>
                      <CreateInvoice
                        dataRef={invoiceDataRef}
                        isEditMode={isEditMode}
                        preId={preId}
                        validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                        timesheetData={timesheetData}
                        invoiceNumber={invoiceNumberFromUrl}
                        onCalculatedSubAmountChange={(data) => {
                          setCalculatedSubAmount(data);
                        }}
                        onDeductionsByFreelancerChange={(data) => {
                          setDeductionsByFreelancer(data);
                        }}
                        onCopiedInvoiceDataChange={(data) => {
                          setCopiedInvoiceData(data);
                        }}
                        onPreferencesChange={(preferences) => {
                          // Update current due date when preferences change
                          if (preferences?.dueDate !== currentDueDate) {
                            setCurrentDueDate(preferences.dueDate || null);
                          }
                        }}
                      />

                    </div>

                    {/* Show modal on top */}
                    {showPreviewModal && (
                      <InvoicePreviewModal
                        isOpen={showPreviewModal}
                        onClose={() => setShowPreviewModal(false)}
                        brandName="Your Company"
                        logoUrl=""
                        invoiceNumber="INV-001"
                        totalAmount="SEK 10,000.00"
                        calculatedSubAmount={calculatedSubAmount}
                        isEditMode={isEditMode}
                        onSendInvoice={handleSendInvoice}
                        isLoading={createLoading}
                        invoiceDay={invoiceDataRef.current?.formData?.invoiceDay || ''}
                        dueDay={invoiceDataRef.current?.formData?.dueDay || ''}
                        deductionsByFreelancer={deductionsByFreelancer}
                        copiedInvoiceData={copiedInvoiceData}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Floating Help Button */}
              {/* <div className="floating-help-button">
              <button
                style={{
                  backgroundColor: "#6750b8",
                  color: "white",
                  border: "none",
                  borderRadius: "24px",
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  boxShadow: "0px 4px 12px rgba(103, 80, 184, 0.3)",
                }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "32px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.31814 11.6819 1.33334 8 1.33334C4.31814 1.33334 1.33334 4.31814 1.33334 8C1.33334 11.6819 4.31814 14.6667 8 14.6667Z" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6.06666 6C6.21993 5.47362 6.56283 5.01615 7.03351 4.7071C7.50419 4.39805 8.07512 4.24885 8.64446 4.28323C9.21379 4.31761 9.75757 4.5334 10.1816 4.89229C10.6056 5.25119 10.8819 5.73073 10.9667 6.25334C10.9667 8 7.99999 9.16667 7.99999 9.16667" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 11.5H8.00667" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {t('NEED_HELP')}
              </button>
            </div> */}

              <InvoiceFooter
                grandTotal="XXXX Kr"
                onSendInvoice={handleSendInvoice}
                isLoading={createLoading}
                isEditMode={isEditMode}
                calculatedSubAmount={calculatedSubAmount}
                selectedCustomer={customerState?.selectedCustomer}
                validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                dataRef={invoiceDataRef}
                  onPreview={handlePreview}
                onSaveDraft={handleSaveDraft}
                isPreviewLoading={preliminaryLoading}
                getPayload={buildInvoicePayload}
                onSecondApiCall={handleSecondApiCall}
                dueDateFromPreferences={currentDueDate || invoiceDataRef.current?.preferences?.dueDate || ''}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateInvoicePage;
